import { WEB_REGISTER } from '../../../web-register.constant'
import { LSPAYErrorMessage } from './lspay.types'

export const LSPAY_PRODUCT_VERSION = `Web ${WEB_REGISTER.CLIENT_VERSION}`

/** The message sent by the SDK on cancelation (we've seen different ones hence the array) */
export const CANCELATION_MESSAGES = [
  'Action canceled by customer.',
  'Transaction is cancelled by the user.',
]

/** The code sent by the SDK on various errors */
export enum STRIPE_ERROR_CODE {
  CANCELLED = 'canceled',
  DECLINED = 'card_declined',
  EXPIRED = 'expired_card',
  INVALID_EXPIRY_YEAR_PAST = 'invalid_expiry_year_past',
  READER_ERROR = 'reader_error',
  FAILED_FETCH_CONNECTION_TOKEN = 'failed_fetch_connection_token',
  INCORRECT_ZIP = 'incorrect_zip',
  INCOMPLETE_ZIP = 'incomplete_zip',
  INCORRECT_NUMBER = 'incorrect_number',
  INVALID_NUMBER = 'invalid_number',
  INCOMPLETE_NUMBER = 'incomplete_number',
  INVALID_EXPIRY_MONTH = 'invalid_expiry_month',
  INVALID_EXPIRY_YEAR = 'invalid_expiry_year',
  INCOMPLETE_EXPIRY = 'incomplete_expiry',
  INCORRECT_CVC = 'incorrect_cvc',
  INVALID_CVC = 'invalid_cvc',
  INCOMPLETE_CVC = 'incomplete_cvc',
  NETWORK_TIMEOUT = 'network_timeout',
  CONNECTION_ERROR = 'connection_error',
  DECLINE_RATE_LIMIT_EXCEEDED = 'card_decline_rate_limit_exceeded',
}

/** Common Stripe validation errors - CNP modal */
export const STRIPE_CNP_VALIDATION_ERRORS = [
  STRIPE_ERROR_CODE.INVALID_NUMBER,
  STRIPE_ERROR_CODE.INVALID_EXPIRY_MONTH,
  STRIPE_ERROR_CODE.INVALID_EXPIRY_YEAR,
  STRIPE_ERROR_CODE.INVALID_CVC,
  STRIPE_ERROR_CODE.INCORRECT_NUMBER,
  STRIPE_ERROR_CODE.INCOMPLETE_NUMBER,
  STRIPE_ERROR_CODE.INCOMPLETE_CVC,
  STRIPE_ERROR_CODE.INCOMPLETE_EXPIRY,
  STRIPE_ERROR_CODE.EXPIRED,
  STRIPE_ERROR_CODE.INCORRECT_CVC,
  STRIPE_ERROR_CODE.INCORRECT_ZIP,
  STRIPE_ERROR_CODE.INCOMPLETE_ZIP,
  STRIPE_ERROR_CODE.INVALID_EXPIRY_YEAR_PAST,
]

/** Stripe errors related to card digits */
export const STRIPE_CARD_DIGITS_ERRORS = [
  STRIPE_ERROR_CODE.INCORRECT_NUMBER,
  STRIPE_ERROR_CODE.INVALID_NUMBER,
  STRIPE_ERROR_CODE.INVALID_EXPIRY_MONTH,
  STRIPE_ERROR_CODE.INVALID_EXPIRY_YEAR,
  STRIPE_ERROR_CODE.INCORRECT_CVC,
  STRIPE_ERROR_CODE.INVALID_CVC,
]
/** Some of the stripe error codes excluded from raygun logging because we can't do much about it */
export const LOGGING_EXCLUDED_STRIPE_ERRORS = [
  STRIPE_ERROR_CODE.INCORRECT_NUMBER,
  STRIPE_ERROR_CODE.INVALID_EXPIRY_MONTH,
  STRIPE_ERROR_CODE.INVALID_EXPIRY_YEAR,
  STRIPE_ERROR_CODE.DECLINED,
  STRIPE_ERROR_CODE.DECLINE_RATE_LIMIT_EXCEEDED,
]

/** Some of the decline codes we have observed and want to handle differently. Note other codes exist ! */
export type STRIPE_DECLINE_CODE =
  | 'insufficient_funds'
  | 'transaction_not_allowed'
  | 'incorrect_pin'
  | 'invalid_account'
  | 'invalid_amount'
  | 'pin_try_exceeded'

export const FAILED_TERMINAL_CONNECTION = '[lspay] Error connecting to terminal'
export const FAILED_REFUND = '[lspay] Error making refund'
export const FAILED_PAIRING_CHECK = '[lspay] error when checking the pairing'
export const FAILED_INTERACT =
  '[lspay] Failed to refund a transaction because the original transaction was completed using an interac card.'
export const PAIRING_ERROR = 'pairing_error'
export const FAILED_CNP_CONFIG = '[lspay] Error fetching cnp config.'
export const FAILED_INTENT = '[lspay] intent error'
export const FAILED_CAPTURE = '[lspay] capture error'
export const FAILED_PERSISTING_CAPTURED_PAYMENT =
  '[lspay] failed to persist automatically captured payment'

export const DEFAULT_TEST_CARD = '4242424242424242'
/** See https://stripe.com/docs/terminal/testing#simulated-test-cards for other cards (to test other brands or failed transactions) */
export const TEST_CARD_LOCAL_STORAGE_KEY = 'VEND_WR_LSPAY_TEST_CARD'

/** Serial Number for the simulator */
export const SIMULATOR_SN = 'SIMULATOR'

/** Feature flag that controls whether to use a simulated reader */
export const SIMULATED_READER_FLAG = 'lspay_simulated_reader'
/**
 * Feature flag that controls whether we target the sandbox or prod of LSPAY.
 * On the frontend, we only use it for logging purposes.
 **/
export const SANDBOX_FLAG = 'swiper_sandbox_lspay'

/** The reason sent by Swiper on decline */

export enum SWIPER_DECLINE_REASON {
  TERMINAL_UNAVAILABLE = 'TERMINAL_UNAVAILABLE',
  UNKNOWN = 'UNKNOWN',
  CARD_DECLINED = 'CARD_DECLINED',
  EXPIRED_CARD = 'EXPIRED_CARD',
  INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
  INVALID_AMOUNT = 'INVALID_AMOUNT',
  INVALID_ACCOUNT = 'INVALID_ACCOUNT',
  TRANSACTION_NOT_ALLOWED = 'TRANSACTION_NOT_ALLOWED',
  INCORRECT_PIN = 'INCORRECT_PIN',
  PIN_TRY_EXCEEDED = 'PIN_TRY_EXCEEDED',
}

/** The subset of reasons sent by Swiper on decline which are card specific. Retrying with the same card is not possible. */
export enum CARD_SPECIFIC_DECLINE_REASON {
  CARD_DECLINED = SWIPER_DECLINE_REASON.CARD_DECLINED,
  TRANSACTION_NOT_ALLOWED = SWIPER_DECLINE_REASON.TRANSACTION_NOT_ALLOWED,
  PIN_TRY_EXCEEDED = SWIPER_DECLINE_REASON.PIN_TRY_EXCEEDED,
  EXPIRED_CARD = SWIPER_DECLINE_REASON.EXPIRED_CARD,
}

/** The reason sent by Swiper on cancel **/
export enum SWIPER_CANCEL_REASON {
  INTENT_CANCELLED = 'INTENT_CANCELLED',
  PAYMENT_INTENT_NOT_FOUND = 'PAYMENT_INTENT_NOT_FOUND',
  SETUP_INTENT_NOT_FOUND = 'SETUP_INTENT_NOT_FOUND',
  MISSING_PAYMENT_METHOD = 'MISSING_PAYMENT_METHOD',
  OVERRIDE_CANCELLED = 'OVERRIDE_CANCELLED',
  MISMATCH_INTENT = 'MISMATCH_INTENT',
  TERMINAL_UNAVAILABLE = 'TERMINAL_UNAVAILABLE',
  TERMINAL_BUSY = 'TERMINAL_BUSY',
  TERMINAL_TIMEOUT = 'TERMINAL_TIMEOUT',
}

export enum LSPAY_ERROR {
  DEFAULT_PAYMENT,
  CANCEL_PAYMENT,
  CANCEL_PAYMENT_WITH_INTENT_CANCELLED,
  CARD_DECLINED,
  CARD_DECLINED_INSUFFICIENT,
  CARD_DECLINED_NOT_ALLOWED,
  CARD_DECLINED_INCORRECT_PIN,
  CARD_DECLINED_INVALID_ACCOUNT,
  CARD_DECLINED_INVALID_AMOUNT,
  CARD_DECLINED_PIN_TRY_EXCEEDED,
  CARD_EXPIRED,
  DEFAULT_REFUND,
  CANCEL_REFUND,
  CANCEL_REFUND_WITH_INTENT_CANCELLED,
  CARD_DECLINED_REFUND,
  CARD_DECLINED_NOT_ALLOWED_REFUND,
  CARD_DECLINED_INCORRECT_PIN_REFUND,
  CARD_DECLINED_INVALID_ACCOUNT_REFUND,
  CARD_DECLINED_PIN_TRY_EXCEEDED_REFUND,
  CARD_EXPIRED_REFUND,
  UNREF_REFUND_NOT_ENABLED,
  PAIRING_MISSING,
  PAIRING_MISSING_REFUND,
  REMOVE_PREVIOUS_CARD,
  READER_BACKGROUND,
  BROWSER_CONNECTIVITY,
  TERMINAL_CONNECTIVITY,
  TERMINAL_DISCONNECTED,
}

export const LSPAY_ERROR_MESSAGES: Record<LSPAY_ERROR, LSPAYErrorMessage> = {
  [LSPAY_ERROR.DEFAULT_PAYMENT]: {
    header: "Something went wrong and this payment hasn't been taken.",
    body:
      'You can retry with the same payment type or choose another payment type to complete this sale. If this keeps happening please contact Lightspeed Retail Support.',
  },
  [LSPAY_ERROR.REMOVE_PREVIOUS_CARD]: {
    header: "Something went wrong and this payment hasn't been taken.",
    body:
      'Cannot start transaction with card inserted from previous transaction, remove card and try again.',
  },
  [LSPAY_ERROR.READER_BACKGROUND]: {
    header: "Something went wrong and this payment hasn't been taken.",
    body:
      'Cannot collect payment while terminal application is in the background. The device may be busy performing updates or changing settings.',
  },
  [LSPAY_ERROR.BROWSER_CONNECTIVITY]: {
    header: "Something went wrong and this payment hasn't been taken.",
    body:
      'Check and ensure your browser is connected to the internet and has a stable connection.',
  },
  [LSPAY_ERROR.TERMINAL_CONNECTIVITY]: {
    header: "Something went wrong and this payment hasn't been taken.",
    body:
      'Check and ensure your terminal is connected to the internet and has a stable connection.',
  },
  [LSPAY_ERROR.TERMINAL_DISCONNECTED]: {
    header: 'The connection to your terminal was lost.',
    body:
      'Check and ensure your terminal is connected to the internet and has a stable connection.',
  },
  [LSPAY_ERROR.CANCEL_PAYMENT]: {
    header: 'This payment has been cancelled.',
    body:
      'You can try with the same payment type or choose another payment type to complete this sale.',
  },
  [LSPAY_ERROR.CANCEL_PAYMENT_WITH_INTENT_CANCELLED]: {
    header: 'This payment was not processed because of a connection issue.',
    body:
      'Ensure your terminal is connected to stable internet and retry the payment.',
  },
  [LSPAY_ERROR.DEFAULT_REFUND]: {
    header: 'Something went wrong and this refund has not been processed.',
    body:
      'You can retry with the same payment type or choose another payment type to complete this refund. If this keeps happening please contact Lightspeed Retail Support.',
  },
  [LSPAY_ERROR.CANCEL_REFUND]: {
    header: 'This refund has been cancelled.',
    body:
      'You can try with the same payment type or choose another payment type to complete this refund',
  },
  [LSPAY_ERROR.CANCEL_REFUND_WITH_INTENT_CANCELLED]: {
    header: `The refund can't be processed because of a connection issue`,
    body:
      'Check and ensure your terminal is connected to the internet and has a stable connection.',
  },
  [LSPAY_ERROR.UNREF_REFUND_NOT_ENABLED]: {
    header: 'The refund can’t be processed',
    body:
      'Your Lightspeed Payments account isn’t currently set up to process refunds that aren’t directly linked to the original payment. You can choose another payment type to process the refund or contract Lightspeed Retail Support to have this feature enabled on your account.',
  },
  [LSPAY_ERROR.CARD_EXPIRED]: {
    header:
      'This payment has been declined because the customer used an expired card.',
    body: 'You can try with a different card or choose another payment type.',
  },
  [LSPAY_ERROR.CARD_DECLINED]: {
    header: 'The payment has been declined.',
    body: 'You can try with a different card or choose another payment type.',
  },
  [LSPAY_ERROR.CARD_DECLINED_INSUFFICIENT]: {
    header:
      "The payment has been declined because the customer's card has insufficient funds.",
    body: 'You can try with a different card or choose another payment type.',
  },
  [LSPAY_ERROR.CARD_DECLINED_INCORRECT_PIN]: {
    header: 'The payment has been declined (incorrect PIN).',
    body: 'You can retry or choose another payment type to complete this sale.',
  },
  [LSPAY_ERROR.CARD_DECLINED_INVALID_ACCOUNT]: {
    header: 'The payment has been declined (invalid account).',
    body: 'You can retry or choose another payment type to complete this sale.',
  },
  [LSPAY_ERROR.CARD_DECLINED_INVALID_AMOUNT]: {
    header: 'The payment has been declined (invalid amount).',
    body: 'You can retry or choose another payment type to complete this sale.',
  },
  [LSPAY_ERROR.CARD_DECLINED_NOT_ALLOWED]: {
    header:
      'The payment has been declined (this card does not support this type of purchase).',
    body:
      'You can retry with a different card or choose another payment type to complete this sale.',
  },
  [LSPAY_ERROR.CARD_DECLINED_PIN_TRY_EXCEEDED]: {
    header:
      'The payment has been declined (allowable number of PIN tries exceeded).',
    body:
      'You can retry with a different card or choose another payment type to complete this sale.',
  },
  [LSPAY_ERROR.PAIRING_MISSING]: {
    header: 'The register is not paired with a terminal.',
    body:
      'To make a payment, pair this register with a terminal in your payment type setup. To complete this sale choose another payment type.',
  },
  [LSPAY_ERROR.PAIRING_MISSING_REFUND]: {
    header: 'The register is not paired with a terminal.',
    body:
      'To make a refund, pair this register with a terminal in your payment type setup. To complete this refund choose another payment type.',
  },
  [LSPAY_ERROR.CARD_DECLINED_REFUND]: {
    header: 'The refund has been declined.',
    body:
      'You can try with a different card or choose another payment type to complete this refund.',
  },
  [LSPAY_ERROR.CARD_EXPIRED_REFUND]: {
    header:
      'The refund has been declined because the customer used an expired card.',
    body:
      'You can try with a different card or choose another payment type to complete this refund.',
  },
  [LSPAY_ERROR.CARD_DECLINED_INCORRECT_PIN_REFUND]: {
    header: 'The refund has been declined (incorrect PIN)',
    body:
      'You can retry or choose another payment type to complete this refund.',
  },
  [LSPAY_ERROR.CARD_DECLINED_PIN_TRY_EXCEEDED_REFUND]: {
    header:
      'The refund has been declined (allowable number of PIN tries exceeded)',
    body:
      'You can retry with a different card or choose another payment type to complete this refund.',
  },
  [LSPAY_ERROR.CARD_DECLINED_NOT_ALLOWED_REFUND]: {
    header:
      'The refund has been declined (this card does not support this type of purchase).',
    body:
      'You can retry with a different card or choose another payment type to complete this refund.',
  },

  [LSPAY_ERROR.CARD_DECLINED_INVALID_ACCOUNT_REFUND]: {
    header: 'The refund has been declined (invalid account)',
    body:
      'You can retry or choose another payment type to complete this refund.',
  },
}

export const SWIPER_ERROR_CODES = {
  CODE_REGISTER_PAIRING_NOT_FOUND: 1500,
  REFUND_AMOUNT_MORE_THAN_ORIGINAL_TRANSACTION: 180002,
  LSPAY_ZERO_DOLLAR_REFUNDABLE: 210002,
  LSPAY_CARD_NOT_PRESENT_INTERAC_REFUND: 210003,
  LSPAY_GET_CARD_NOT_PRESENT_CONFIG_NOT_FOUND: 210006,
  LSPAY_AMOUNT_TOO_SMALL: 210007,
  LSPAY_CARD_INCORRECT_ZIP: 210019,
  LSPAY_CARD_INSUFFICIENT_FUND: 210020,
  LSPAY_CARD_AUTH_REQUIRED: 210021,
  LSPAY_CARD_INCORRECT_CVC: 210022,
  LSPAY_CARD_EXPIRED: 210023,
  LSPAY_CONSENT_NOT_COLLECTED: 210061,
  LSPAY_REF_REFUND_INSUFFICIENT_AMOUNT: 210063,
  LSPAY_UNREF_REFUND_SETTINGS_NOT_FOUND: 210066,
  LSPAY_UNREF_REFUND_EXCEED_LIMIT: 210067,
  LSPAY_UNREF_REFUND_NOT_ENABLED: 210068,
}

export enum LSPAY_STEPS {
  DISCOVER = 'discover',
  CONNECT = 'connect',
  COLLECT = 'collect',
  PROCESS = 'process',
  CAPTURE = 'capture',
  CNP_FETCH_CONFIG = 'cnp_fetch_config',
  CNP_CREATE_INTENT = 'cnp_create_intent',
  CNP_PROCESS = 'cnp_process',
  CNP_CAPTURE = 'cnp_capture',
}

export enum LSPAY_TRANSACTION_STATUS {
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  DECLINED = 'DECLINED',
  CANCELLED = 'CANCELLED',
  AUTHORISED = 'AUTHORISED',
}

export enum LSPAY_REFUND_TRANSACTION_STATUS {
  PROCESSING = 'PROCESSING',
  CARD_PRESENTED = 'CARD_PRESENTED',
  COMPLETED = 'COMPLETED',
  DECLINED = 'DECLINED',
  CANCELLED = 'CANCELLED',
}

export enum LSPAY_SERVER_DRIVEN_SUPPORTED_DEVICES {
  BBPOS_WISEPOS_E = 'bbpos_wisepos_e',
  BBPOS_WISEPOS_E_DEVKIT = 'bbpos_wisepos_e_devkit',
  STRIPE_S700 = 'stripe_s700',
  STRIPE_S700_DEVKIT = 'stripe_s700_devkit',
  SIMULATED_WISEPOS_E = 'simulated_wisepos_e',
  SIMULATED_S700 = 'simulated_s700',
}
export enum TRANSACTION_TYPE {
  REFUND = 'refund',
  PAYMENT = 'payment',
}
